<script>
import api from "@/helpers/api";

/**
 * Sticky on-screen action bar for the unified print routes. Hidden on print
 * via `.d-print-none`. Buttons:
 *   - Print: window.print() — primary on desktop
 *   - Download PDF: GET /api/print/render?type=...&id=... → Blob → anchor
 *   - Share: navigator.share with the PDF blob (mobile primary)
 *   - Close: window.close() — only useful when opened in a new tab
 *
 * Detection of mobile uses pointer:coarse + UA-CH (when present); raw
 * userAgent string sniffing avoided.
 */
export default {
  name: "PrintActionBar",
  props: {
    printType: { type: String, required: true },
    printId: { type: [String, Number], required: true },
    fileName: { type: String, default: "document.pdf" },
    showClose: { type: Boolean, default: true },
  },
  data() {
    return {
      busy: false,
      isMobile: false,
      canShareFiles: false,
    };
  },
  mounted() {
    this.detectPlatform();
  },
  methods: {
    detectPlatform() {
      const coarseAndNarrow =
        window.matchMedia &&
        window.matchMedia("(pointer: coarse) and (max-width: 900px)").matches;
      const uaCh = navigator.userAgentData?.mobile === true;
      this.isMobile = !!(coarseAndNarrow || uaCh);

      try {
        // canShare with files probe — we'll only actually have a file at
        // share time, so this is a capability check only.
        this.canShareFiles = !!(navigator.canShare && navigator.share);
      } catch (_) {
        this.canShareFiles = false;
      }
    },

    async fetchPdfBlob() {
      const { data } = await api.get("/print/render", {
        params: { type: this.printType, id: this.printId, format: "pdf" },
        responseType: "blob",
      });
      return data;
    },

    async onPrint() {
      window.print();
    },

    async onDownload() {
      this.busy = true;
      try {
        const blob = await this.fetchPdfBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        console.warn("Download PDF failed:", err);
        alert("Could not download PDF. Try Print instead.");
      } finally {
        this.busy = false;
      }
    },

    async onShare() {
      this.busy = true;
      try {
        const blob = await this.fetchPdfBlob();
        const file = new File([blob], this.fileName, { type: "application/pdf" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: this.fileName,
            text: this.fileName,
          });
        } else {
          // Fallback: download — user can attach manually.
          this.onDownload();
        }
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.warn("Share failed:", err);
        }
      } finally {
        this.busy = false;
      }
    },

    onClose() {
      window.close();
    },
  },
};
</script>

<template>
  <div class="print-action-bar d-print-none">
    <div class="print-action-bar-inner">
      <button
        type="button"
        class="print-btn print-btn-primary"
        :class="{ secondary: isMobile }"
        :disabled="busy"
        @click="onPrint"
      >
        <i class="ri-printer-line" /> Print
      </button>

      <button
        type="button"
        class="print-btn"
        :disabled="busy"
        @click="onDownload"
      >
        <i class="ri-download-2-line" /> Download PDF
      </button>

      <button
        v-if="canShareFiles"
        type="button"
        class="print-btn"
        :class="{ 'print-btn-primary': isMobile }"
        :disabled="busy"
        @click="onShare"
      >
        <i class="ri-share-line" /> Share
      </button>

      <button
        v-if="showClose"
        type="button"
        class="print-btn print-btn-ghost"
        :disabled="busy"
        @click="onClose"
      >
        Close
      </button>
    </div>
  </div>
</template>

<style scoped>
.print-action-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  border-bottom: 1px solid #E5E7EB;
  padding: 8px 16px;
  margin-bottom: 8px;
}

.print-action-bar-inner {
  max-width: 220mm;
  margin: 0 auto;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.print-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #D1D5DB;
  background: #FFFFFF;
  color: #1A1A1A;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: var(--print-font, system-ui);
}

.print-btn:hover { background: #F3F4F6; }
.print-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.print-btn-primary {
  background: var(--print-accent, #1F4E79);
  color: #FFFFFF;
  border-color: var(--print-accent, #1F4E79);
}
.print-btn-primary:hover {
  filter: brightness(1.1);
  background: var(--print-accent, #1F4E79);
}

.print-btn-primary.secondary {
  background: #FFFFFF;
  color: #1A1A1A;
  border-color: #D1D5DB;
}

.print-btn-ghost { border-color: transparent; }

@media (max-width: 600px) {
  .print-btn { flex: 1 1 auto; justify-content: center; }
}
</style>
